import React, { useCallback, useContext, useMemo } from 'react'

import Cookie from 'js-cookie'
import { useSelector } from 'react-redux'
import { Tag, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faCheckDouble, faClinicMedical, faEdit, faEnvelope, faEnvelopeCircleCheck, faFile, faInfo, faInfoCircle, faTimes } from '@fortawesome/pro-solid-svg-icons'

import { OrderBy } from '../../../../utils/constants'
import {
  getComment,
  getCreatedAt,
  getEmail,
  getEndDate,
  getFirstname,
  getInternshipYear,
  getLastname,
  getRequestOrigin,
  getSchoolName,
  getSectionAbbreviation,
  getSectionName,
  getSectorName,
  getInstitutionName,
  getStartDate,
  InternshipRequest,
  Origins,
  States,
  getInstitutionFromRequest,
  institutionAnswerStands,
  schoolAnswerStands,
  getStateTag,
  StateTypes
} from '../../../../utils/entities/internshipRequest'
import { IndividualInternshipRequestModalContext, ModalTypes } from '../../../../Providers/Forms/IndividualInternshipRequest/IndividualInternshipRequestModal'
import { InternshipRequestFilesDrawerContext } from './InternshipRequestFilesDrawer'
import { Action, DeleteAction } from '../../../../Components/antd/Tables/DataTable/utils/actions'
import DataTable from '../../../../Components/antd/Tables/DataTable/DataTable'
import { isInstitutionGroupAdmin, isInstitutionUser, isSchoolUser } from '../../../../utils/roles'
import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import { selectActiveLanguageCode } from '../../../../reducers/LanguageReducer'
import { selectUser } from '../../../../reducers/UserReducer'
import { isObject } from '../../../../utils'
import { InstitutionModalContext } from '../../../../Providers/Forms/InstitutionModal/InstitutionModal'
import { buildFilters, Filter } from '../../../../Components/antd/Tables/DataTable/utils/tableFilter'
import InternshipRequestFilters, { getActiveFiltersCount } from './InternshipRequestFilters'
import { InstitutionContext } from '../../../../Providers/InstitutionProvider'
import { validateInternshipRequestByTrainingGround } from '../../../../utils/api/internshipRequest'
import { getErrorBody, onError, onSuccess } from '../../../../utils/apiHelper'
import { HttpStatuses } from '../../../../utils/http'
import { ColorsPalette } from '../../../../config/colors'

const OPTION_NOT_ACTIVATED = 'INSTITUTION_DOES_NOT_USE_SECTOR_APPROVAL'

const sectorValidationEnabledStates = [States.ON_HOLD, States.ACTION_REQUIRED]

const InternshipsRequestsTable = ({
  data, institution = null, school = null, loading, metadata, parameters, onDelete, onParametersChange, onRefuse, reload, onLinkInstitution
}) => {
  const { setHandler } = useContext(IndividualInternshipRequestModalContext)
  const filesContext = useContext(InternshipRequestFilesDrawerContext)
  const institutionModalContext = useContext(InstitutionModalContext)
  const { useTrainingGroundValidation } = useContext(InstitutionContext) ?? {}

  const user = useSelector(selectUser)
  const t = useSelector(selectTranslate)
  const currentLanguage = useSelector(selectActiveLanguageCode)

  const maxHeight = useMemo(() => `calc(100vh - ${Cookie.get('switch_user_id') ? '235px' : '194px'})`, [Cookie.get('switch_user_id')])
  const sendApprovalEmail = useCallback(internshipRequest => {
    validateInternshipRequestByTrainingGround(user, internshipRequest).then(() => {
      reload()
      onSuccess(t('internship_requests_table.actions.send_approval_email.success'))
    }).catch(error => {
      if (error.status === HttpStatuses.BAD_REQUEST) {
        getErrorBody(error).then(errorBody => {
          if (errorBody.message === OPTION_NOT_ACTIVATED) {
            onError(t(`internship_requests_table.actions.send_approval_email.error.${errorBody.message.toLowerCase()}`))
          } else {
            onError(t('internship_requests_table.actions.send_approval_email.error'))
          }
        })
      } else {
        onError(t('internship_requests_table.actions.send_approval_email.error'))
      }
    })
  }, [user.id, reload, t])
  const partnerAnswered = useCallback(
    r => isSchoolUser(user) ? institutionAnswerStands(r) : schoolAnswerStands(r),
    [user]
  )
  const validationLocked = useCallback(
    r => StateTypes.CLOSED.values.includes(r.state) || (isSchoolUser(user) ? schoolAnswerStands(r) : institutionAnswerStands(r)),
    [user]
  )
  const openAnswerModal = useCallback(
    i => setHandler({ data: new InternshipRequest({ ...i }), callback: reload, type: ModalTypes.ACCEPT }),
    [setHandler, reload]
  )
  const columns = useMemo(() => {
    const userIsInstitutionGroupAdmin = isInstitutionGroupAdmin(user)
    const contextColumn = isInstitutionUser(user)
      ? { title: t('data_table.column_title.school'), orderBy: OrderBy.SCHOOL, key: getSchoolName }
      : { title: t('data_table.column_title.institution'), orderBy: OrderBy.INSTITUTION, key: getInstitutionName }

    const columns = [
      {
        title: t('data_table.column_title.state'),
        orderBy: OrderBy.STATE,
        key: r => {
          const tag = getStateTag(r.state)

          return (
            <>
              <Tag color={tag.color}> {t(tag.label)} </Tag>
              {r.state === States.REFUSED && r.refusalReason && (
                <Tooltip title={r.refusalReason}>
                  <FontAwesomeIcon icon={faInfoCircle} color={ColorsPalette.SECOND} />
                </Tooltip>
              )}
            </>
          )
        }
      },
      { title: t('data_table.column_title.creation_date'), orderBy: OrderBy.CREATED_AT, key: getCreatedAt },
      { title: t('data_table.column_title.section'), orderBy: OrderBy.SECTION, key: getSectionAbbreviation, tooltip: getSectionName },
      { title: t('data_table.column_title.internship_year'), orderBy: OrderBy.SCHOOL_YEAR, key: getInternshipYear },
      { title: t('data_table.column_title.start_date'), orderBy: OrderBy.START_DATE, key: getStartDate },
      { title: t('data_table.column_title.end_date'), orderBy: OrderBy.END_DATE, key: getEndDate },
      { title: t('data_table.column_title.lastname'), orderBy: OrderBy.LAST_NAME, key: getLastname },
      { title: t('data_table.column_title.firstname'), orderBy: OrderBy.FIRST_NAME, key: getFirstname },
      { title: t('data_table.column_title.email'), orderBy: OrderBy.EMAIL, key: getEmail }
    ]

    if (userIsInstitutionGroupAdmin) {
      columns.push({
        title: t('Site'),
        orderBy: OrderBy.INSTITUTION,
        key: r => getInstitutionName(r) ?? r.institutionName
      })
    }

    columns.push(
      { title: t('data_table.column_title.sector'), orderBy: OrderBy.SECTOR, key: getSectorName },
      contextColumn,
      { title: t('data_table.column_title.comment'), orderBy: OrderBy.COMMENT, key: getComment },
      { title: t('data_table.column_title.request_origin'), orderBy: OrderBy.REQUEST_ORIGIN, key: r => t('internship_request.origine.' + getRequestOrigin(r).toLowerCase()) }
    )

    return columns
  }, [user, t])
  const actions = useMemo(() => [
    new Action({
      toRender: r => r.state === States.INCOMPLETE && isObject(institutionModalContext),
      className: 'ant-warning-button',
      title: 'internship_requests_table.actions.complete',
      icon: faClinicMedical,
      onClick: i => institutionModalContext.setFormHandler(form => ({
        ...form,
        data: getInstitutionFromRequest(i),
        callback: (institution, sector) => onLinkInstitution(i, institution, sector),
        type: ModalTypes.INSTITUTION_VALIDATION
      }))
    }),
    new Action({
      toRender: r => r.state !== States.INCOMPLETE,
      disabled: r => StateTypes.CLOSED.values.includes(r.state),
      title: 'internship_requests_table.actions.edit',
      icon: faEdit,
      onClick: i => setHandler({ data: new InternshipRequest({ ...i }), callback: reload, type: ModalTypes.FORM })
    }),
    new Action({
      toRender: partnerAnswered,
      disabled: validationLocked,
      className: 'ant-green-button',
      title: 'internship_requests_table.actions.confirm_partner_answer',
      icon: faCheckDouble,
      onClick: openAnswerModal
    }),
    new Action({
      toRender: r => !partnerAnswered(r),
      disabled: validationLocked,
      title: 'internship_requests_table.actions.accept_internship_request',
      icon: faCheck,
      onClick: openAnswerModal
    }),
    new Action({
      title: 'internship_requests_table.actions.consult_files',
      className: d => d.files.length ? 'ant-green-button' : '',
      icon: faFile,
      onClick: i => filesContext.setHandler({ data: new InternshipRequest({ ...i }), callback: reload })
    }),
    new Action({
      disabled: r => StateTypes.CLOSED.values.includes(r.state),
      title: 'internship_requests_table.actions.consult_student_data',
      icon: faEnvelope,
      onClick: i => setHandler({ data: new InternshipRequest({ ...i }), callback: reload, type: ModalTypes.DISPLAY_STUDENT })
    }),
    new Action({
      toRender: r => !!r.metadata,
      disabled: r => StateTypes.CLOSED.values.includes(r.state),
      title: 'internship_requests_table.actions.consult_metadata',
      icon: faInfo,
      onClick: i => setHandler({ data: new InternshipRequest({ ...i }), callback: reload, type: ModalTypes.DISPLAY_METADATA })
    }),
    new Action({
      toRender: () => useTrainingGroundValidation || isInstitutionGroupAdmin(user),
      disabled: r => !sectorValidationEnabledStates.includes(r.state),
      title: 'internship_requests_table.actions.send_approval_email',
      icon: faEnvelopeCircleCheck,
      onClick: sendApprovalEmail
    }),
    new Action({
      disabled: r => StateTypes.CLOSED.values.includes(r.state),
      onClick: onRefuse,
      icon: faTimes,
      title: 'internship_requests_table.actions.refuse_title',
      buttonType: 'danger'
    }),
    new DeleteAction({
      disabled: r => r.requestOrigin !== Origins.MANUAL || StateTypes.CLOSED.values.includes(r.state),
      onDelete
    })
  ], [filesContext.setHandler, sendApprovalEmail, setHandler, reload, user, useTrainingGroundValidation, onDelete, onRefuse, partnerAnswered, validationLocked, openAnswerModal])

  const filters = useMemo(() => buildFilters([
    new Filter({
      key: 'filters',
      label: t('common.filters'),
      component: () => <InternshipRequestFilters filters={parameters?.filters ?? { states: [] }} onFiltersChange={filters => onParametersChange({ ...parameters, filters })} />
    })
  ]), [onParametersChange, parameters?.filters, t])

  const handleCreate = useCallback(() => setHandler({
    data: new InternshipRequest({ requestOrigin: Origins.MANUAL, institution, school, language: { languageTag: currentLanguage } }),
    callback: reload,
    type: ModalTypes.FORM
  }), [user, reload, institution, school, currentLanguage])

  const handleResetFilters = useCallback(() => {
    onParametersChange({ ...parameters, filters: { states: [] } })
  }, [onParametersChange, parameters])

  return (
    <div style={{ height: maxHeight }}>
      <DataTable
        actions={actions}
        filters={filters}
        columns={columns}
        data={data}
        loading={loading}
        disabledAdd={loading}
        metadata={metadata}
        parameters={parameters}
        getActiveFiltersCount={getActiveFiltersCount}
        onCreate={isSchoolUser(user) ? null : handleCreate}
        onParametersChange={onParametersChange}
        onResetFilters={handleResetFilters}
      />
    </div>
  )
}

export default InternshipsRequestsTable
